import { FC, useEffect, useMemo, useState } from "react";
import { Box, Grid, LinearProgress, Stack } from "@mui/material";
import { useUserStore } from "../../../../../stores/user.store";
import useIncentiveDisbursementReport from "../../../../../hooks/useIncentiveDisbursementReport";
import BasicKPIComponent from "../../../../Dashboard/BasicKPIComponent";
import DataTable from "../../../../../components/DataTable/DataTable";
import { DataTableCell } from "../../../../../components/DataTable/dataTable.interface";
import EarnedVsPotentialChart from "../../../../../components/EarnedVsPotentialChart";
import ReportFiltersBar, {
	ReportFilters,
	toApiDate,
} from "../../components/ReportFiltersBar";
import { useParticipantColumn } from "../../useParticipantColumn";
import EngagementRateBadge from "./EngagementRateBadge";
import { useFormatCurrency } from "../../../../../hooks/useSetting";

// SIMS-8895 (3)(4): same two fixes as the Enrollment summary tab — the
// Enrollment ID column is dropped rather than truncated, and every text column
// states its header alignment, which DataTableHead would otherwise default to
// the right for a non-sortable column while the body cell renders left.
const buildHeadCells = (participantLabel: string) => [
	{
		id: "participantName",
		label: participantLabel,
		isSortable: false,
		disablePadding: false,
		align: "left" as const,
	},
	{
		id: "orgUnit",
		label: "Org unit",
		isSortable: false,
		disablePadding: false,
		align: "left" as const,
	},
	{
		id: "researcher",
		label: "Researcher",
		isSortable: false,
		disablePadding: false,
		align: "left" as const,
	},
	{
		id: "potential",
		label: "Potential",
		isSortable: false,
		disablePadding: false,
		align: "right" as const,
	},
	{
		id: "earned",
		label: "Earned",
		isSortable: false,
		disablePadding: false,
		align: "right" as const,
	},
	{
		id: "received",
		label: "Received",
		isSortable: false,
		disablePadding: false,
		align: "right" as const,
	},
	{
		id: "engagementRate",
		label: "Engagement",
		isSortable: false,
		disablePadding: false,
		align: "center" as const,
	},
];

/**
 * SIMS-5825: the four report filters are owned by the page and shared by every
 * tab, so the single Cover sheet in the export describes the whole workbook
 * truthfully. Pagination stays local — it is a view position, not a filter.
 */
interface IncentiveDisbursementTabProps {
	programId: string;
	filters: ReportFilters;
	onFiltersChange: (next: ReportFilters) => void;
}

const IncentiveDisbursementTab: FC<IncentiveDisbursementTabProps> = ({
	programId,
	filters,
	onFiltersChange,
}) => {
	const { userInfo } = useUserStore();
	const accountId = userInfo?.accountId ?? "";
	const formatCurrency = useFormatCurrency();
	const participant = useParticipantColumn();
	const headCells = useMemo(
		() => buildHeadCells(participant.label),
		[participant.label],
	);

	const [page, setPage] = useState<number>(0);
	const [pageSize, setPageSize] = useState<number>(10);

	// A filter change invalidates the current page — page 3 of the old result
	// set is meaningless against the new one.
	useEffect(() => {
		setPage(0);
	}, [filters]);

	const { data, isLoading, isFetching } = useIncentiveDisbursementReport({
		accountId,
		programId,
		orgUnitId: filters.orgUnitId || undefined,
		researcherId: filters.researcherId || undefined,
		startDate: toApiDate(filters.startDate),
		endDate: toApiDate(filters.endDate),
		pageNumber: page,
		pageSize,
	});

	const rows = useMemo<DataTableCell[]>(() => {
		return (data?.byEnrollment ?? []).map((e, idx) => ({
			id: idx,
			// SIMS-8895 (5): name or CIN per the account setting, empty when neither.
			participantName: participant.display(e.participantName, e.participantCin),
			orgUnit: e.orgUnit,
			researcher: e.researcher,
			potential: formatCurrency(e.potential),
			earned: formatCurrency(e.earned),
			received: formatCurrency(e.received),
			engagementRate: {
				id: e.enrollmentId,
				node: <EngagementRateBadge rate={e.engagementRate} />,
			},
		}));
	}, [data, formatCurrency, participant]);

	return (
		<Stack spacing={3}>
			<ReportFiltersBar
				idPrefix="incentive-disbursement"
				filters={filters}
				onChange={onFiltersChange}
				availableOrgUnits={data?.availableOrgUnits ?? []}
				availableResearchers={data?.availableResearchers ?? []}
			/>

			{(isLoading || isFetching) && <LinearProgress />}

			{/* KPI cards */}
			<Grid container spacing={2}>
				<BasicKPIComponent
					title="Total earned"
					content={formatCurrency(data?.kpis.totalEarned)}
					footer={{ hidden: true }}
				/>
				<BasicKPIComponent
					title="Total received"
					content={formatCurrency(data?.kpis.totalReceived)}
					footer={{ hidden: true }}
				/>
				<BasicKPIComponent
					title="Avg earned per enrollment"
					content={formatCurrency(data?.kpis.avgEarned)}
					footer={{ hidden: true }}
				/>
				<BasicKPIComponent
					title="Potential total"
					content={formatCurrency(data?.kpis.potential)}
					footer={{ hidden: true }}
				/>
				<BasicKPIComponent
					title="Engagement rate"
					content={`${(data?.kpis.engagementRate ?? 0).toFixed(1)}%`}
					footer={{ hidden: true }}
				/>
			</Grid>

			{/* Earned vs Potential per day (reuse existing component).
			    The chart card no longer carries its own top margin — SIMS-7548 moved
			    spacing to the caller so it can sit in a gapped layout — so the separation
			    it used to provide is set here. */}
			<Box sx={{ mt: 3 }}>
				<EarnedVsPotentialChart data={data?.byDay} isLoading={isLoading} />
			</Box>

			{/* Per-enrollment breakdown */}
			<Box>
				<DataTable
					data={rows}
					rowsPerPage={pageSize}
					order="desc"
					orderBy="earned"
					page={page}
					headCells={headCells}
					total={data?.pagination.total ?? 0}
					isLoading={isLoading || isFetching}
					messageEmptyData="No enrollments match the selected filters"
					onChangePage={(_event, newPage) => setPage(newPage)}
					onChangeSize={(event) => {
						setPage(0);
						setPageSize(Number(event.target.value));
					}}
				/>
			</Box>
		</Stack>
	);
};

export default IncentiveDisbursementTab;
